/** Map tiles from Bason's own server (BR-143). A plain <img> can't carry the access token a rider may sign in with
 * (only the session cookie), so each tile is fetched like any other API call and shown as a blob. When the server
 * says it is busy (503 with Retry-After) or the hourly limit is near (429), the tile is asked for again after a
 * short pause instead of staying grey; Leaflet itself never retries a failed tile. */
import L from "leaflet";
import { useEffect } from "react";
import { useMap } from "react-leaflet";
import { API_BASE, getToken } from "@/api/client";

const URL_TEMPLATE = `${API_BASE}/tiles/{z}/{x}/{y}.png`;
const ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const TRIES = 5;
const PARALLEL = 4; // the server fetches at most 4 new tiles at once: asking for more only earns "busy"

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** At most PARALLEL tile requests in flight per page; the rest wait their turn (first asked, first served). */
let running = 0;
const waiting: (() => void)[] = [];
async function turn<T>(work: () => Promise<T>): Promise<T> {
  if (running >= PARALLEL) await new Promise<void>((go) => waiting.push(go));
  running += 1;
  try {
    return await work();
  } finally {
    running -= 1;
    waiting.shift()?.();
  }
}

/** One tile: fetched with the rider's sign-in. "Busy" (503/429 with a short Retry-After) is asked again after a
 * slightly random pause, so many tiles don't come back at the same moment; a long Retry-After (OSM paused, the
 * hour used up) or any other answer ends it. */
export async function fetchTile(url: string, signal?: AbortSignal): Promise<Blob> {
  for (let attempt = 0; ; attempt++) {
    const res = await turn(() => {
      const token = getToken();
      return fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, signal });
    });
    if (res.ok) return res.blob();
    const after = Number(res.headers.get("Retry-After") || "1");
    const busy = (res.status === 503 || res.status === 429) && after <= 10;
    if (!busy || attempt >= TRIES - 1) throw new Error(`tile ${res.status}`);
    await sleep(after * 1000 * (1 + attempt * 0.5) * (0.5 + Math.random()));
  }
}

class AuthTileLayer extends L.TileLayer {
  private aborts = new Map<HTMLElement, AbortController>();
  private blobs = new Map<HTMLElement, string>();

  constructor(url: string, options?: L.TileLayerOptions) {
    super(url, options);
    // a tile scrolled out of view (or dropped while zooming) stops loading and gives its picture back
    const drop = (e: L.TileEvent) => {
      this.aborts.get(e.tile)?.abort();
      this.release(e.tile);
    };
    this.on("tileunload", drop);
    this.on("tileabort", drop);
  }

  private release(img: HTMLElement): void {
    const src = this.blobs.get(img);
    if (src) URL.revokeObjectURL(src);
    this.blobs.delete(img);
  }

  override createTile(coords: L.Coords, done: L.DoneCallback): HTMLElement {
    const img = document.createElement("img");
    img.alt = "";
    img.setAttribute("role", "presentation");
    const ctl = new AbortController();
    this.aborts.set(img, ctl);
    fetchTile(this.getTileUrl(coords), ctl.signal)
      .then((blob) => {
        if (ctl.signal.aborted) return;
        const src = URL.createObjectURL(blob);
        this.blobs.set(img, src);
        img.onload = () => {
          this.release(img);
          done(undefined, img);
        };
        img.onerror = () => {
          this.release(img);
          done(new Error("tile image"), img);
        };
        img.src = src;
      })
      .catch((e: unknown) => {
        if (!ctl.signal.aborted) done(e instanceof Error ? e : new Error(String(e)), img);
      })
      .finally(() => this.aborts.delete(img));
    return img;
  }
}

/** The OpenStreetMap layer of every Bason map, served through `/api/v1/tiles`. */
export function AuthTiles() {
  const map = useMap();
  useEffect(() => {
    const layer = new AuthTileLayer(URL_TEMPLATE, { attribution: ATTRIBUTION, maxZoom: 19 }).addTo(map);
    return () => {
      layer.remove();
    };
  }, [map]);
  return null;
}
